/**
 * The plain-English rules summary of a competition, built from the competition's own fields (pool, window, type, winners, refund date, rules hash).
 * Pure: no React. It is a summary of the on-chain rules, not Terms of Service, and it says what the contract's rules file says (the published JSON,
 * whose keccak256 is the rules hash stored on-chain).
 */
export interface RulesInput {
  type: string
  /** "CADINUdex V3 · CBON/USDT 0.25%" */
  poolLabel: string
  projectSymbol: string
  startTime: number
  endTime: number
  refundAfter: number
  winners: number
  rulesHash: string
}

export const RULES_FILES: Record<string, string> = {
  VOLUME: '/competition-rules/volume.rules.json',
  PURCHASE_HOLD: '/competition-rules/purchase-hold.rules.json',
}
export const rulesFileOf = (type: string) => RULES_FILES[type] ?? null

const pad = (n: number) => String(n).padStart(2, '0')
/** "2026-10-01 00:04 UTC" and the viewer's own clock ("03:34 local"): the page shows both. */
export function whenBoth(ts: number, tz?: string): { utc: string; local: string } {
  const d = new Date(ts * 1000)
  const utc = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`
  const local = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: tz, timeZoneName: 'short' }).format(d)
  return { utc, local }
}

export interface RuleItem {
  id: string
  title: string
  text: string
}

export function rulesSummary(r: RulesInput): RuleItem[] {
  const a = whenBoth(r.startTime)
  const b = whenBoth(r.endTime)
  const refund = whenBoth(r.refundAfter)
  const sym = r.projectSymbol || 'the project token'
  const score =
    r.type === 'PURCHASE_HOLD'
      ? `Score is the ${sym} you BUY through this pool, multiplied by how long you keep it until the end. Selling or moving the tokens out removes their score, oldest purchases first. Tokens you already held do not count.`
      : `Score is your gross trading volume of ${sym} through this pool: buys plus sells. Repeating trades raises it. It measures activity, not unique people.`
  return [
    {
      id: 'pool-window',
      title: 'Only this pool, only this window',
      text: `Only trades through ${r.poolLabel} count, and only between ${a.utc} (${a.local}) and ${b.utc} (${b.local}). A trade routed through any other pool does not count.`,
    },
    {
      id: 'wallet',
      title: 'Which wallet is credited',
      text: 'The wallet that sends the transaction (the transaction origin) is credited. Trades made elsewhere through this pool also count: this page\'s trade panel is a convenience, not a requirement.',
    },
    { id: 'score', title: r.type === 'PURCHASE_HOLD' ? 'Buy and hold' : 'Trading volume', text: score },
    {
      id: 'prize',
      title: 'The prize',
      text: `The prize is split in proportion to score among the top ${r.winners}. Nothing guarantees you a share.`,
    },
    {
      id: 'refund',
      title: 'If no result is published',
      text: `If no result is published by ${refund.utc} (${refund.local}), the whole prize returns to the creator.`,
    },
    {
      id: 'risk',
      title: 'Risks',
      text: 'Thin liquidity and price impact can cost you real money, a trade cannot be undone, and you can lose on every trade and still win nothing.',
    },
  ]
}

/** 0x6593…9fed */
export const shortHash = (h: string) => (h && h.length > 14 ? `${h.slice(0, 6)}…${h.slice(-4)}` : h)
